import {
	type SuretyAccountId,
	type SuretyQuoteId,
	suretyAccountId,
	suretyQuoteId,
} from "@inrev/common";
import { useAdminFetchAccount } from "@portal/api/account/admin";
import {
	useFetchAdminBondRequest,
	useFetchAdminQuoteBondForm,
} from "@portal/api/bond-request/admin";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { useRecentlyViewedEntities } from "@portal/hooks/useRecentlyViewed";
import { type FC, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { AdminBondRequestTabbedLayout } from "./tabbed/AdminBondRequestTabbedLayout";

const AdminBondRequestContent: FC<{
	accountId: SuretyAccountId;
	id: SuretyQuoteId;
}> = ({ accountId, id }) => {
	const navigate = useNavigate();
	const { bondRequest, bondRequestLoading, bondRequestError } = useFetchAdminBondRequest(
		accountId,
		id,
	);
	const bondFormQuery = useFetchAdminQuoteBondForm(accountId, id);
	const { account, accountError, accountIsLoading } = useAdminFetchAccount(accountId);
	const { addRecentlyViewedEntity } = useRecentlyViewedEntities();

	useEffect(() => {
		if (bondRequest !== undefined && bondRequest.status === "bound") {
			navigate(`/bonds/${bondRequest.bondId}`, { replace: true });
		}
	}, [bondRequest, navigate]);

	useEffect(() => {
		if (bondRequest) {
			addRecentlyViewedEntity({ id: bondRequest.id, entityType: "bondRequest" });
		}
	}, [bondRequest, addRecentlyViewedEntity]);

	useEffect(() => {
		if (bondRequestError || accountError) {
			navigate("/requests", { replace: true });
		}
	}, [bondRequestError, accountError, navigate]);

	if (
		bondRequestLoading ||
		accountIsLoading ||
		bondRequest === undefined ||
		bondRequest.status === "bound" ||
		account === undefined
	) {
		return <LoadingModal />;
	}

	return (
		<AdminBondRequestTabbedLayout
			request={bondRequest}
			bondFormQuery={bondFormQuery}
			account={account}
		/>
	);
};

export const AdminBondRequestView = () => {
	const params = useParams();
	const accountIdResult = suretyAccountId.safeParse(params.accountId);
	const quoteIdResult = suretyQuoteId.safeParse(params.id);

	if (!accountIdResult.success || !quoteIdResult.success) {
		return <Navigate to="/requests" replace />;
	}

	return <AdminBondRequestContent accountId={accountIdResult.data} id={quoteIdResult.data} />;
};
